import { VerificationStatus } from "shared/types";
import api from "api/landlord/applications";
import { CreditReport, VerificationReport } from "types/landlord/reports";
import { ReportHeader } from "../../components/ReportHeader";
import { CreditScore } from "./components/CreditScore";
import { PersonalData } from "./components/PersonalData";
import { BankruptcyDetails } from "./components/BankruptcyDetails";
import { ConsumerInformation } from "./components/ConsumerInformation";
// import { AccountsSummary } from "../../slices/CreditReportSlice/components/AccountsSummary";
import { ConsumerStatement } from "./components/ConsumerStatement";
import { TrdPartyCollectionDetails } from "./components/TrdPartyCollectionDetails";
import { HistoricalConsumerInformation } from "./components/HistoricalConsumerInformation";
import { AlertsAndTriggers } from "./components/AlertsAndTriggers";
import { AccountsOverview } from "./components/AccountsOverview";
import { EmploymentInformation } from "./components/EmploymentInformation";
// import { TradesSummaryAndAccountStatus } from "../../slices/CreditReportSlice/components/TradesSummaryAndAccountStatus";
import { CreditInquiries } from "./components/CreditInquiries";
import { useQuery } from "@tanstack/react-query";
import { CreditReportSummary } from "./components/CreditReportSummary";
import { AccountsSummary } from "./components/AccountsSummary";
import { ReportException } from "views/applications-id-report/components/ReportException";
import { Loader } from "shared/components/Loader";
import { PrintReady } from "views/applications-id-report/providers/PrintProvider";
import { PotentialNegativeInfo } from "./components/PotentialNegativeInfo";
import { CollectionsSummary } from "views/applications-id-report/slices/CreditReportSlice/components/CollectionsSummary";
import { errorHitCodes } from "./utils";
import { ApplicantSettings } from "shared/types";
import { getFullName } from "shared/utils/get-full-name";
import { Applicant } from "types/application";

type CreditReportSliceProps = {
  applicant: Applicant;
  verificationId: number;
  pastDueAccountType: string;
  screeningCriteria?: ApplicantSettings;
};

export const CreditReportSlice = ({
  applicant,
  verificationId,
  pastDueAccountType,
  screeningCriteria
}: CreditReportSliceProps) => {
  const { data: creditReport, isFetched } = useQuery<
    VerificationReport<CreditReport>
  >({
    queryKey: [
      "CREDIT_REPORT",
      applicant.application_id,
      verificationId,
      applicant.id
    ],
    queryFn: () =>
      api
        .getApplicantVerification(
          applicant.application_id,
          applicant.id,
          verificationId
        )
        .then((res) => res.verification)
  });

  const { report } = creditReport ?? {};

  const isNoHit =
    report?.score == 4 ||
    (report?.hit_code?.code == "2" && report.hit_code.description == "No-Hit");

  const {
    personal_data,
    consumer_information,
    account_overview,
    alerts_and_triggers,
    credit_inquiries,
    historical_consumer_information,
    employment_information,
    bankruptcy_details,
    consumer_statements,
    collections,
    past_due_accounts = [],
    balance_past_due,
    balance_of_collections,
    debt_balance,
    criteria
  } = report ?? {};

  const bankruptcyYears = Number(
    applicant.template_criteria_settings?.bankruptcy_records?.years
  );
  const bankruptcyCriteriaValue =
    applicant.criteria_check_settings?.bankruptcy_records.value;

  if (!isFetched)
    return (
      <>
        <ReportHeader title="Credit report" verificationId={verificationId} />
        <Loader />
      </>
    );

  if (
    Object.keys(report || {}).length === 0 ||
    errorHitCodes.includes(report?.hit_code?.code || "") ||
    creditReport?.status === VerificationStatus.Failed
  )
    return (
      <ReportException
        title="Credit report"
        verificationId={verificationId}
        hitCode={report?.hit_code}
      />
    );

  return (
    <>
      <ReportHeader
        title="Credit report"
        reportDate={creditReport?.updated_at}
        verificationId={verificationId}
      />

      <PrintReady verificationId={verificationId} />

      <CreditReportSummary
        creditScore={report?.score}
        bankruptcy={bankruptcy_details?.length}
        bankruptcyYears={bankruptcyYears}
        bankruptcyCriteriaValue={String(bankruptcyCriteriaValue)}
        pastDueAccount={
          pastDueAccountType === "Percentage" && account_overview
            ? (past_due_accounts.length / account_overview.length) * 100
            : past_due_accounts.length
        }
        balanceOfUnpaidCollections={
          balance_past_due && balance_past_due.cents / 100
        }
        balanceOfCollections={
          balance_of_collections && balance_of_collections.cents / 100
        }
        criteria={criteria}
        verificationId={verificationId}
        completed_at={creditReport?.completed_at}
        isNoHit={isNoHit}
        debtBalance={debt_balance && debt_balance.cents / 100}
        applicantName={getFullName({
          firstName: applicant.first_name,
          lastName: applicant.last_name,
          middleName: applicant.middle_name,
          short: true
        })}
        pastDueAccountType={pastDueAccountType}
        screeningCriteria={screeningCriteria}
      />

      <PersonalData personal_data={personal_data} isNoHit={isNoHit} />

      <ConsumerInformation
        consumer_information={consumer_information}
        isNoHit={isNoHit}
      />

      <CreditScore score={Number(report?.score)} isNoHit={isNoHit} />

      <AccountsSummary accounts={account_overview} isNoHit={isNoHit} />

      <PotentialNegativeInfo
        accounts={account_overview}
        isNoHit={isNoHit}
        bankruptciesNumber={bankruptcy_details?.length}
        collectionsNumber={collections?.length}
      />

      <BankruptcyDetails bankrupcies={bankruptcy_details} isNoHit={isNoHit} />

      {/* No data yet */}
      {/* <TradesSummaryAndAccountStatus /> */}

      <AccountsOverview
        account_overview={account_overview}
        isNoHit={isNoHit}
        verificationId={verificationId}
      />
      <EmploymentInformation
        employment_information={employment_information}
        isNoHit={isNoHit}
      />
      <AlertsAndTriggers
        alerts_and_triggers={alerts_and_triggers}
        isNoHit={isNoHit}
      />
      <ConsumerStatement
        consumer_statements={consumer_statements}
        isNoHit={isNoHit}
      />

      <CollectionsSummary isNoHit={isNoHit} collections={collections} />

      <TrdPartyCollectionDetails collections={collections} isNoHit={isNoHit} />
      <CreditInquiries credit_inquiries={credit_inquiries} isNoHit={isNoHit} />
      <HistoricalConsumerInformation
        historical_consumer_information={historical_consumer_information}
        isNoHit={isNoHit}
      />
    </>
  );
};
